import { Editor, Node, Path, Element as SlateElement, Transforms } from 'slate'

import {
  BLOCK_FORMATS,
  CustomEditor,
} from 'core-components/library/richTextEditor/interface/custom-types'
import { TCell } from 'core-components/library/richTextEditor/interface/TCell'

import { IPrevValues } from '../models/ITables'

import { IAddSelectionProps } from './../models/ITables'
import { getCol, getGridTable, getStartCell, isTable, TableCol } from './commonTableHelper'

const prepareRowSelection = (editor: CustomEditor, coverCols: TableCol[]) => {
  const { xIndex, yIndex } = getGridTable(editor)

  const selectedRowNumbers = new Set()
  const colSpans = new Map()

  const preparedCoverCols = coverCols.filter((col, index, cols) => {
    return (
      index === cols.findIndex((currentCul) => Path.equals(currentCul.originPath, col.originPath))
    )
  })

  preparedCoverCols.forEach((col) => selectedRowNumbers.add(col.originPath[yIndex]))
  selectedRowNumbers.forEach((rowNumber) => {
    preparedCoverCols.forEach((col) => {
      if (col.originPath[yIndex] === rowNumber) {
        const colSpanAmount = colSpans.get(rowNumber)
        colSpans.set(rowNumber, colSpanAmount ? colSpanAmount + col.cell.colspan : col.cell.colspan)
      }
    })
  })

  const maxColAmount = Math.max(...colSpans.values())
  let maxColIndex = 0
  const indexesOfMinValues: number[] = []
  colSpans.forEach((value, index) => {
    if (value < maxColAmount) {
      indexesOfMinValues.push(index)
    } else {
      maxColIndex = index
    }
  })

  const rowWithMaxCol: number[] = []
  preparedCoverCols.forEach((col) => {
    if (col.originPath[yIndex] === maxColIndex) {
      rowWithMaxCol.push(col.originPath[xIndex])
    }
  })

  const minPathInMaxColRow = Math.min(...rowWithMaxCol)
  const maxPathInMaxColRow = minPathInMaxColRow + colSpans.get(maxColIndex) - 1

  if (indexesOfMinValues.length) {
    indexesOfMinValues.forEach((value) => {
      const currentRow: number[] = []
      preparedCoverCols.forEach((col) => {
        if (col.originPath[yIndex] === value) {
          currentRow.push(col.originPath[xIndex])
        }
      })

      const minPathInCurrentRow = Math.min(...currentRow)
      const maxPathInCurrentRow = minPathInCurrentRow + colSpans.get(value) - 1

      const differencesBetweenMin = minPathInCurrentRow - minPathInMaxColRow
      const differencesBetweenMax = maxPathInMaxColRow - maxPathInCurrentRow

      if (differencesBetweenMin > 0) {
        let path: number[] = []
        preparedCoverCols.forEach((col) => {
          if (col.originPath[yIndex] === value) {
            if (col.originPath[xIndex] === minPathInCurrentRow) {
              path = col.originPath
            }
          }
        })

        for (let i = 0; i < differencesBetweenMin; i++) {
          try {
            Transforms.setNodes(
              editor,
              {
                selectedCell: true,
              },
              {
                at: Path.previous(path),
                match: (node) =>
                  SlateElement.isElement(node) && node.type === BLOCK_FORMATS.tableCell,
              }
            )
            path = Path.previous(path)
          } catch (e) {
            console.log(e)
          }
        }
      }

      if (differencesBetweenMax > 0) {
        let path: number[] = []
        preparedCoverCols.forEach((col) => {
          if (col.originPath[yIndex] === value) {
            if (col.originPath[xIndex] <= maxPathInCurrentRow && !col.cell.selectedCell) {
              path = col.originPath
            }
          }
        })

        for (let i = 0; i < differencesBetweenMax; i++) {
          try {
            Transforms.setNodes(
              editor,
              {
                selectedCell: true,
              },
              {
                at: Path.next(path),
                match: (node) =>
                  SlateElement.isElement(node) && node.type === BLOCK_FORMATS.tableCell,
              }
            )
            const selectedNode = Node.get(editor, Path.next(path))
            if ((selectedNode as TCell).colspan > 1) {
              i += (selectedNode as TCell).colspan - 1
            }
            path = Path.next(path)
          } catch (e) {
            console.log(e)
          }
        }
      }
    })
  }
}

export function addSelection({
  editor,
  startPath,
  endPath,
  prevSelectedPaths,
  changePrevSelectedPaths,
}: IAddSelectionProps): TableCol[] {
  if (
    JSON.stringify(prevSelectedPaths.endPath) === JSON.stringify(endPath) &&
    JSON.stringify(prevSelectedPaths.startPath) === JSON.stringify(startPath)
  ) {
    return prevSelectedPaths.res
  }

  removeSelection(editor, changePrevSelectedPaths)

  if (!editor.selection || !isTable(editor)) {
    return []
  }

  const { gridTable } = getGridTable(editor)
  const startCell = getStartCell(editor)
  if (!startCell || !gridTable) {
    return []
  }
  const [head] = getCol(
    gridTable,
    (c: TableCol) => Path.equals(Editor.path(editor, c.originPath), startPath) && c.isReal
  )
  const [tail] = getCol(
    gridTable,
    (c: TableCol) => Path.equals(Editor.path(editor, c.originPath), endPath) && c.isReal
  )

  if (!tail || !head) {
    return []
  }

  const { path: tailPath } = tail
  const { path: headPath } = head
  headPath.forEach((item: number, index: number) => {
    headPath[index] = Math.min(item, tailPath[index])
    tailPath[index] = Math.max(item, tailPath[index])
  })

  const coverCols: TableCol[] = []

  gridTable.forEach((row: TableCol[]) => {
    row.forEach((col: TableCol) => {
      const { path } = col
      const isOver = path.findIndex((item, index) => {
        if (item < headPath[index] || item > tailPath[index]) {
          return true
        }
        return false
      })
      if (isOver < 0) {
        coverCols.push(col)
      }
    })
  })

  coverCols.forEach(({ originPath }) => {
    Transforms.setNodes(
      editor,
      {
        selectedCell: true,
      },
      {
        at: originPath,
        match: (node) => SlateElement.isElement(node) && node.type === BLOCK_FORMATS.tableCell,
      }
    )
  })

  prepareRowSelection(editor, coverCols)

  changePrevSelectedPaths({ startPath: [...startPath], endPath: [...endPath], res: coverCols })

  return coverCols
}

export function removeSelection(
  editor: Editor,
  changePrevSelectedPaths: (value: IPrevValues) => void
) {
  changePrevSelectedPaths({ startPath: [], endPath: [], res: [] })
  Transforms.unsetNodes(editor, 'selectedCell', {
    at: [],
    match: (node) => SlateElement.isElement(node) && !!(node as TCell).selectedCell,
  })
}
